import {
  CurrentDeliveryStatsDto,
  CurrentOrderStatsDto,
  KnownDeliveryType,
  KnownProjectDtoDisplayWorkflowTypesItem
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo } from 'react';
import { useDisplayWorkflowTypes } from '../../../hooks/use-display-workflow-types.hook';
import { useQueryDeliveryStats, useQueryOrderStats } from '../dashboard.queries';

export function useCurrentStats(projectID: string) {
  // When no projects with a Retail workflow are in scope we will display the Delivery State Count Grid
  // When projects with a Retail workflow are in scope we will display the Order Status Count Grid, this includes when there are multiple workflows in scope
  // When we display the Order Status Count Grid we aggregate the results from the Current Delivery Stats and the Current Retail Order stats endpoints
  // The Current Delivery Stats endpoint also counts Retail Order Deliveries so when we display the Order Status Count Grid we need to filter out Retail Order Deliveries from that count
  const [displayWorkflowTypes] = useDisplayWorkflowTypes(projectID);

  const excludeRetailDeliveriesFromCurrentStats = displayWorkflowTypes.includes(
    KnownProjectDtoDisplayWorkflowTypesItem.WorkflowRetail
  );

  let deliveryTypesFilter: KnownDeliveryType[] | undefined = undefined;

  if (excludeRetailDeliveriesFromCurrentStats) {
    deliveryTypesFilter = Object.values(KnownDeliveryType).filter((type) => type !== KnownDeliveryType.RetailOrder);
  }

  const currentRetailOrderStats = useQueryOrderStats({ projectID });
  const currentDeliveryStats = useQueryDeliveryStats({
    projectID,
    deliveryTypes: deliveryTypesFilter
  });

  const currentMergedStats = useMemo(() => {
    if (currentRetailOrderStats.isLoading || currentDeliveryStats.isLoading) {
      return { isLoading: true, data: undefined } as const;
    }
    return {
      isLoading: false,
      data: mergeCurrentStats(currentRetailOrderStats.data, currentDeliveryStats.data)
    } as const;
  }, [
    currentDeliveryStats.data,
    currentDeliveryStats.isLoading,
    currentRetailOrderStats.data,
    currentRetailOrderStats.isLoading
  ]);

  return {
    currentRetailOrderStats,
    currentDeliveryStats,
    currentMergedStats
  };
}

export function mergeCurrentStats(
  currentRetailStats?: CurrentOrderStatsDto,
  currentDeliveryStats?: CurrentDeliveryStatsDto
): CurrentOrderStatsDto {
  const total = (currentRetailStats?.total || 0) + (currentDeliveryStats?.total || 0);
  const statuses: (keyof CurrentOrderStatsDto['statuses'])[] = [
    'booked',
    'cancelled',
    'collected',
    'delivered',
    'inProgress',
    'overdue',
    'retrievalRequired',
    'retrieved'
  ];
  const statusCounts = statuses.reduce((acc, key) => {
    const retailCount = currentRetailStats?.statuses[key] || 0;
    const deliveryCount = key === 'booked' ? 0 : currentDeliveryStats?.states[key] || 0;
    const sum = retailCount + deliveryCount;
    return { ...acc, [key]: sum };
  }, {} as CurrentOrderStatsDto['statuses']);
  return {
    total,
    statuses: statusCounts
  };
}
